import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
// import axios from 'axios'; // Hapus import yang tidak digunakan

const Documents = () => {
  // Change from:
  const { /* user, token */ } = useAuth();
  
  // To:
  const auth = useAuth();
  // Then use auth.user and auth.token if needed
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, /* setFilterType */ ] = useState(''); // Hapus variabel yang tidak digunakan
  const [openDialog, setOpenDialog] = useState(false); // Define the missing state
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    // Simulasi fetch data dokumen
    const fetchDocuments = async () => {
      try {
        // Di sini Anda akan memanggil API untuk mendapatkan data dokumen
        // Untuk sementara, kita gunakan data dummy
        setTimeout(() => {
          setDocuments([
            { id: 1, name: 'Dokumen Kontrak', type: 'PDF', size: '2.5 MB', uploadedBy: 'Admin', uploadDate: '2023-05-10', projectId: 1, projectName: 'Proyek A', status: 'approved' },
            { id: 2, name: 'Gambar Teknis', type: 'DWG', size: '5.1 MB', uploadedBy: 'Project Manager', uploadDate: '2023-05-12', projectId: 1, projectName: 'Proyek A', status: 'pending' },
            { id: 3, name: 'Jadwal Proyek', type: 'XLSX', size: '1.2 MB', uploadedBy: 'Admin', uploadDate: '2023-05-15', projectId: 2, projectName: 'Proyek B', status: 'approved' },
            { id: 4, name: 'Foto Lokasi', type: 'JPG', size: '3.7 MB', uploadedBy: 'Site Manager', uploadDate: '2023-05-16', projectId: 2, projectName: 'Proyek B', status: 'approved' },
            { id: 5, name: 'Laporan Kemajuan', type: 'PDF', size: '4.2 MB', uploadedBy: 'Site Engineer', uploadDate: '2023-05-18', projectId: 3, projectName: 'Proyek C', status: 'approved' }
          ]);
          setLoading(false);
        }, 1000);

        // Simulasi fetch data proyek
        setTimeout(() => {
          setProjects([
            { id: 1, name: 'Proyek A' },
            { id: 2, name: 'Proyek B' },
            { id: 3, name: 'Proyek C' }
          ]);
        }, 800);
      } catch (error) {
        console.error('Error fetching documents:', error);
        enqueueSnackbar('Gagal memuat data dokumen', { variant: 'error' });
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [enqueueSnackbar]);

  // Get file icon based on type
  const getFileIcon = (type) => {
    switch(type.toUpperCase()) {
      case 'PDF':
        return <PictureAsPdfIcon color="error" />;
      case 'JPG':
      case 'PNG':
      case 'GIF':
        return <ImageIcon color="primary" />;
      default:
        return <InsertDriveFileIcon color="action" />;
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.projectName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType ? doc.type.toLowerCase() === filterType.toLowerCase() : true;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <Container maxWidth="xl">
        <PageHeader 
          title="Document Management" 
          icon={<DescriptionIcon fontSize="large" />}
          description="Kelola dan akses dokumen proyek"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="Document Management" 
        icon={<DescriptionIcon fontSize="large" />}
        description="Kelola dan akses dokumen proyek"
      />

      {/* Action Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ bgcolor: '#F0C14B', '&:hover': { bgcolor: '#DDA726' } }}
          >
            Unggah Dokumen
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{ color: '#5D4037', borderColor: '#5D4037' }}
          >
            Filter
          </Button>
        </Box>
        <TextField
          placeholder="Cari dokumen..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Document Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#E3F2FD', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Total Dokumen
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {documents.length}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Chip label="Aktif" color="primary" size="small" />
                <Typography variant="body2">
                  {projects.length} Proyek
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#FFF8E1', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Dokumen Terbaru
              </Typography>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {documents.length > 0 ? documents[documents.length - 1].name : '-'}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Diunggah pada: {documents.length > 0 ? documents[documents.length - 1].uploadDate : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#E8F5E9', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Dokumen Menunggu Persetujuan
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {documents.filter(doc => doc.status === 'pending').length}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Dari total {documents.length} dokumen
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Documents Table */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daftar Dokumen
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dokumen</TableCell>
                    <TableCell>Proyek</TableCell>
                    <TableCell>Tipe</TableCell>
                    <TableCell>Ukuran</TableCell>
                    <TableCell>Tanggal</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIcon(doc.type)}
                          <Typography variant="body2">{doc.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{doc.projectName}</TableCell>
                      <TableCell>{doc.type}</TableCell>
                      <TableCell>{doc.size}</TableCell>
                      <TableCell>{doc.uploadDate}</TableCell>
                      <TableCell>
                        <Chip 
                          label={doc.status === 'approved' ? 'Disetujui' : 'Menunggu'} 
                          color={doc.status === 'approved' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Lihat Dokumen">
                            <IconButton size="small" color="primary">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unduh Dokumen">
                            <IconButton size="small" color="secondary">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Hapus Dokumen">
                            <IconButton size="small" color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Documents;
